import React from 'react';
import {
  VISA_TYPE_OPTIONS,
  VISA_STATUS_OPTIONS,
  YES_NO_NOT_REQUIRED,
  YES_NO_OPTIONS,
} from '../constants/visaFormOptions';
import CounselorSearchSelect from './CounselorSearchSelect';
import CountrySearchSelect from './CountrySearchSelect';

const subheadingStyle = {
  fontSize: '14px',
  fontWeight: 600,
  color: '#374151',
  margin: '8px 0 14px',
  paddingLeft: '12px',
  borderLeft: '3px solid #F47B20',
  width: '100%',
};

export default function VisaFormFields({
  data,
  onChange,
  countries = [],
  counselors = [],
  disabled = false,
}) {
  const showDependent = data.visaType === 'Dependent Visa';

  const handleSelectChange = (e) => {
    const { name, value } = e.target;
    onChange(e);

    // Clear dependent date fields when parent is not Yes
    if (name === 'visaAppointment' && value !== 'Yes' && data.visaAppointmentDate) {
      onChange({ target: { name: 'visaAppointmentDate', value: '' } });
    }
    if (name === 'biometrics' && value !== 'Yes' && data.biometricsAppointmentDate) {
      onChange({ target: { name: 'biometricsAppointmentDate', value: '' } });
    }
    if (name === 'medicalBooked' && value !== 'Yes') {
      if (data.medicalAppointmentDate) {
        onChange({ target: { name: 'medicalAppointmentDate', value: '' } });
      }
      if (data.medicalTest) {
        onChange({ target: { name: 'medicalTest', value: '' } });
      }
    }
    if (name === 'bankStatementRequirement' && value !== 'Yes' && data.bankStatementAmountRequired) {
      onChange({ target: { name: 'bankStatementAmountRequired', value: '' } });
    }
  };

  const field = (name, label, { type = 'text', required = false, col = 6, full = false } = {}) => (
    <div className={`${full ? 'col-12' : `col-md-${col}`} mb-3`}>
      <label className="form-label" htmlFor={name}>
        {label}
        {required && <span className="required">*</span>}
      </label>
      <input
        type={type}
        id={name}
        name={name}
        className="input-field"
        value={data[name] || ''}
        onChange={onChange}
        required={required}
        disabled={disabled}
      />
    </div>
  );

  const select = (name, label, options, { required = false, col = 6, placeholder = 'Select' } = {}) => (
    <div className={`col-md-${col} mb-3`}>
      <label className="form-label" htmlFor={name}>
        {label}
        {required && <span className="required">*</span>}
      </label>
      <select
        id={name}
        name={name}
        className="input-field"
        value={data[name] || ''}
        onChange={handleSelectChange}
        required={required}
        disabled={disabled}
      >
        <option value="">{placeholder}</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
    </div>
  );

  return (
    <>
      <div className="form-section">
        <h2 className="section-title">Student Visa Details</h2>
        <div className="row">
          {field('applicantName', 'Student Name', { required: true, col: 4 })}
          {field('applicantEmail', 'Email', { type: 'email', col: 4 })}
          {field('applicantPhone', 'Phone Number', { type: 'tel', col: 4 })}
          <div className="col-md-6 mb-3">
            <label className="form-label" htmlFor="countryId">
              Country <span className="required">*</span>
            </label>
            <CountrySearchSelect
              countries={countries}
              value={data.countryId || ''}
              onChange={(countryId) => onChange({ target: { name: 'countryId', value: countryId } })}
              required
              disabled={disabled}
              placeholder="Type to search country..."
            />
          </div>
          {select('visaType', 'Visa Type', VISA_TYPE_OPTIONS, { required: true, col: 6 })}
        </div>
      </div>

      {showDependent && (
        <div className="form-section">
          <h2 className="section-title">Dependent Visa — Main Applicant</h2>
          <div className="row">
            {field('mainApplicantName', 'Main Applicant Name of Dependent', { col: 6 })}
            {field('mainApplicantRelation', 'Relation', { col: 6 })}
            {field('mainApplicantInstitute', "Main Applicant's Institute Name", { col: 6 })}
            {field('mainApplicantVisaCategory', 'Visa Category of the Main Applicant', { col: 6 })}
            {field('mainApplicantVisaStatus', 'Visa Status of the Main Applicant', { col: 6 })}
          </div>
        </div>
      )}

      <div className="form-section">
        <h2 className="section-title">Visa Information</h2>
        <div className="row">
          {field('visaCategory', 'Visa Category', { col: 4 })}
          {select('visaStatus', 'Visa Status', VISA_STATUS_OPTIONS, { required: true, col: 4 })}
          {field('submissionDate', 'Visa Submission Date', { type: 'date', col: 4 })}
        </div>

        <div className="row">
          <div className="col-12">
            <div style={subheadingStyle}>Visa Appointment</div>
          </div>
          {select('visaAppointment', 'Visa Appointment', YES_NO_NOT_REQUIRED, { col: 6 })}
          {data.visaAppointment === 'Yes' &&
            field('visaAppointmentDate', 'Visa Appointment Date', { type: 'date', col: 6 })}
        </div>

        <div className="row">
          <div className="col-12">
            <div style={subheadingStyle}>Biometrics</div>
          </div>
          {select('biometrics', 'Biometrics', YES_NO_NOT_REQUIRED, { col: 6 })}
          {data.biometrics === 'Yes' &&
            field('biometricsAppointmentDate', 'Biometrics Appointment Date', { type: 'date', col: 6 })}
        </div>

        <div className="row">
          <div className="col-12">
            <div style={subheadingStyle}>Medical</div>
          </div>
          {select('medicalBooked', 'Medical', YES_NO_NOT_REQUIRED, { col: 4 })}
          {data.medicalBooked === 'Yes' && (
            <>
              {field('medicalTest', 'Medical Test', { col: 4 })}
              {field('medicalAppointmentDate', 'Medical Appointment Date', { type: 'date', col: 4 })}
            </>
          )}
        </div>

        <div className="row">
          <div className="col-12">
            <div style={subheadingStyle}>Bank Statement</div>
          </div>
          {select('bankStatementRequirement', 'Bank Statement Requirement', YES_NO_NOT_REQUIRED, { col: 6 })}
          {data.bankStatementRequirement === 'Yes' &&
            field('bankStatementAmountRequired', 'Bank Statement Amount Required', { col: 6 })}
        </div>

        <div className="row">
          <div className="col-md-6 mb-3">
            <label className="form-label" htmlFor="submittedBy">
              Visa Application Submitted by
            </label>
            <CounselorSearchSelect
              counselors={counselors}
              value={data.submittedBy || ''}
              disabled={disabled}
              placeholder="Search counselor or select Other..."
              onChange={(value) =>
                onChange({ target: { name: 'submittedBy', value } })
              }
            />
          </div>
          {field('visaNumber', 'Visa ID Number', { col: 6 })}
          {field('institute', 'Institute Name', { col: 6 })}
          {select('accommodationBooked', 'Accommodation Booked', YES_NO_OPTIONS, { col: 6 })}
          {field('visaLink', 'Visa Website Link', { col: 12 })}
          {field('visaWebsiteId', 'Visa Website Id', { col: 6 })}
          {field('visaPassword', 'Visa Website Password', { col: 6 })}
        </div>
      </div>
    </>
  );
}
